/**
 *  Copyright (C) 2022 - Jeci SARL - https://jeci.fr
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU Affero General Public License as
 *  published by the Free Software Foundation, either version 3 of the
 *  License, or (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU Affero General Public License for more details.
 *
 *  You should have received a copy of the GNU Affero General Public License
 *  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */

import { ALIAS_MAP } from "@/services/aliasMap.js";

/**
 * Stateless service for building AFTS (Alfresco Full-Text Search) queries
 * from a structured JSON configuration (locations + criteria).
 */
class AftsQueryBuilder {
  /**
   * Maps a property alias to its Solr field name.
   * Returns the original property if no alias mapping is found.
   * @param {string} prop - The property name or alias.
   * @returns {string} The resolved Solr field name.
   */
  mapAliasToSolr(prop) {
    if (!prop) {
      return prop;
    }
    const propLower = prop.toLowerCase();
    return ALIAS_MAP[propLower] || prop;
  }

  /**
   * Builds an AFTS query string from a structured JSON configuration.
   *
   * The queryJson object should have the following structure:
   * {
   *   locations: [{ type: 'Site'|'Folder', value: string, negation: boolean }],
   *   criteria: [{
   *     property: { id: string, input: string },
   *     operator: string,
   *     negation: boolean,
   *     value: any,
   *     value_end?: any
   *   }]
   * }
   *
   * Also supports legacy criteria format with a raw `query` property:
   * { query: "TYPE:'cm:content'" }
   *
   * @param {Object} queryJson - The structured query configuration.
   * @param {Array} [queryJson.locations] - Array of location filters.
   * @param {Array} [queryJson.criteria] - Array of search criteria.
   * @returns {string} The constructed AFTS query string.
   */
  buildAftsQueryFromJSON(queryJson) {
    if (!queryJson) {
      return "";
    }

    const queryParts = [];

    // Build location parts: inclusions OR'd, exclusions AND NOT'd
    const locations = queryJson.locations || [];
    const inclusions = locations.filter((loc) => !loc.negation);
    const exclusions = locations.filter((loc) => loc.negation);

    if (inclusions.length > 0) {
      const inclusionParts = inclusions
        .map((loc) => {
          if (loc.type === "Site" && loc.value) {
            return `SITE:"${loc.value}"`;
          } else if (loc.type === "Folder" && loc.value) {
            return `ANCESTOR:'${loc.value}'`;
          }
          return null;
        })
        .filter(Boolean);
      if (inclusionParts.length === 1) {
        queryParts.push(inclusionParts[0]);
      } else if (inclusionParts.length > 1) {
        queryParts.push(`(${inclusionParts.join(" OR ")})`);
      }
    }

    exclusions.forEach((loc) => {
      if (loc.type === "Site" && loc.value) {
        queryParts.push(`NOT SITE:"${loc.value}"`);
      } else if (loc.type === "Folder" && loc.value) {
        queryParts.push(`NOT ANCESTOR:'${loc.value}'`);
      }
    });

    // Build criteria parts — support grouped format and legacy flat format
    if (queryJson.criteriaGroups) {
      const groupParts = this._buildGroupsParts(queryJson.criteriaGroups);
      if (groupParts) {
        queryParts.push(groupParts);
      }
    } else {
      const criteria = queryJson.criteria || [];
      const criteriaParts = criteria
        .map((c) => this._buildCriterionPart(c))
        .filter(Boolean);
      if (criteriaParts.length > 0) {
        queryParts.push(criteriaParts.join(" AND "));
      }
    }

    return queryParts.join(" AND ");
  }

  /**
   * Builds an AFTS query fragment for a single criterion.
   * @param {Object} c - A criterion object.
   * @returns {string|null} The AFTS fragment, or null if invalid.
   */
  _buildCriterionPart(c) {
    // Support legacy format with raw query string
    if (c.query) {
      return c.query;
    }

    if (c.value === null || c.value === "") {
      return null;
    }

    const prefix = c.negation ? "NOT " : "";
    let valuePart = "";

    const prop = this.mapAliasToSolr(c.property?.id);
    const inputType = c.property?.input;

    switch (inputType) {
      case "boolean":
        valuePart = `${prop}:${c.value}`;
        break;

      case "date":
      case "datetime":
        if (c.operator === "range") {
          let startISO = "MIN";
          if (c.value) {
            const startOfDay = new Date(c.value);
            startOfDay.setHours(0, 0, 0, 0);
            startISO = startOfDay.toISOString();
          }

          let endISO = "MAX";
          if (c.value_end) {
            const endOfDay = new Date(c.value_end);
            endOfDay.setHours(23, 59, 59, 999);
            endISO = endOfDay.toISOString();
          }
          valuePart = `${prop}:['${startISO}' TO '${endISO}']`;
        } else {
          const dayStart = new Date(c.value);
          dayStart.setHours(0, 0, 0, 0);
          const dayEnd = new Date(c.value);
          dayEnd.setHours(23, 59, 59, 999);
          valuePart = `[${prop}:'${dayStart.toISOString()}' TO '${dayEnd.toISOString()}']`;
        }
        break;

      default:
        if (c.operator === "exact") {
          valuePart = `${prop}:"${c.value}"`;
        } else if (c.operator === "contains") {
          valuePart = `${prop}:"*${c.value}*"`;
        } else if (c.operator === "range") {
          const start = c.value !== null ? c.value : "MIN";
          const end = c.value_end !== null ? c.value_end : "MAX";
          valuePart = `${prop}:[${start} TO ${end}]`;
        }
        break;
    }

    if (valuePart) {
      return prefix ? `${prefix}(${valuePart})` : valuePart;
    }
    return null;
  }

  /**
   * Builds an AFTS query for a single group of criteria.
   * @param {Object} group - A criteria group with operator and criteria array.
   * @returns {string|null} The AFTS fragment for the group, or null if empty.
   */
  _buildGroupQuery(group) {
    const parts = group.criteria
      .map((c) => this._buildCriterionPart(c))
      .filter(Boolean);
    if (parts.length === 0) {
      return null;
    }
    if (parts.length === 1) {
      return parts[0];
    }
    return `(${parts.join(` ${group.operator} `)})`;
  }

  /**
   * Builds the criteria portion of the AFTS query from criteriaGroups.
   * @param {Object} criteriaGroups - The grouped criteria structure.
   * @returns {string|null} The AFTS fragment for all groups, or null if empty.
   */
  _buildGroupsParts(criteriaGroups) {
    const groupParts = criteriaGroups.groups
      .map((g) => this._buildGroupQuery(g))
      .filter(Boolean);
    if (groupParts.length === 0) {
      return null;
    }
    if (groupParts.length === 1) {
      return groupParts[0];
    }
    return groupParts.join(` ${criteriaGroups.rootOperator} `);
  }
}

export default new AftsQueryBuilder();
